import { useEffect, useState } from 'react';
import Fuse from 'fuse.js';

import Checkbox from '@klv/molecules/form-field/checkbox/checkbox';
import FilterAccordion from '@klv/atoms/filter-accordion';
import Buttons from '@klv/molecules/buttons';
import { Wrapper } from '@klv/utils/wrapper';
import { generateClassName } from '@klv/shared/utils/functions';
import useCollapseRefinements from '@klv/hooks/useCollapseRefinements';
import { useTranslation } from '@klv/hooks/useTranslation';

import FilterSearchInput from './filter-search-input';
import PostCard from './post-card';
import * as styles from './styles/postFilter.module.scss';

import type { IPostFilter, ITermNode, IPostFilterPost, ITaxonomyRegister } from './types';


const taxonomyRegister: ITaxonomyRegister = {
  resource: ['ResourceCategory', 'ResourceCollection', 'ResourceStage', 'ResourceTopic'],
};

export const PostFilter = ({
  className = '',
  ctaButtons,
  ctaHeadline,
  ctaSubtext,
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  fadeIn,
  headline,
  posts,
  postType,
  subheadline,
  taxonomies,
  ...props
}: IPostFilter) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ITermNode[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<IPostFilterPost[]>(posts);
  
  // Clears all selected filters and unchecks all checkboxes
  const clearAll = () => {
    setFilters([]);
    setSearchTerm('');
    document.querySelectorAll('.unifiedSearchFilters input[type="checkbox"]').forEach((checkbox) => {
      (checkbox as HTMLInputElement).checked = false;
    });
  };

  // Updates the filters state based on checkbox selection
  const updateFilters = (checked: boolean, slug: string, nodeType: string) => {
    if (!checked) {
      setFilters(filters.filter((filter) => !(filter.slug === slug && filter.nodeType === nodeType)));
    } else {
      setFilters([...filters, { slug, nodeType }]);
    }
  };

  // Handles collapsing of filter accordions
  const handleCollapseRefinements = useCollapseRefinements();

  // Updates filtered posts based on selected filters
  useEffect(() => {
    let filtered = posts;
    if (filters.length > 0) {
      filtered = posts.filter(post => {
        return post?.filters?.some(postFilter => 
          filters.some(filter => {
            return filter.slug === postFilter.slug && filter.nodeType === postFilter.nodeType;
          }),
        );
      });
    }
    
    // use the fuse search library to search the filtered posts
    if (searchTerm.length > 2) {
      filtered = new Fuse(
        filtered,
        {
          isCaseSensitive: false,
          keys: [
            'title',
            'excerpt',
          ],
        },
      ).search(searchTerm).map((result) => result.item);
    }
    setFilteredPosts(filtered);
  }, [filters, searchTerm, posts]);

  return (
    <Wrapper
      backgroundColor='lightSand'
      contained
      inlinePadding
      fadeIn={0}
      grid
      customPadding={customPadding}
      className={generateClassName([className, styles.postFilter])}
      {...props}
    >
      <div className={generateClassName(['h3', styles.header])}>{headline}</div>
      <div className={styles.filters}>
        <div className={styles.accordionToggles}>
          <button 
            className={styles.toggleButton} 
            type="button" 
            onClick={handleCollapseRefinements}>
              {t('pages_search_filters_collapseAll')}
          </button>
          <button 
            className={styles.toggleButton}
            type="button"
            onClick={clearAll}>
              {t('pages_search_filters_clearAll')}
          </button>
        </div>
        {/** Taxonomy checkboxes go here. */}
        <div className="unifiedSearchFilters">
        {taxonomyRegister[postType].map((category: string) => (
            <FilterAccordion 
            className={`${styles.filterAccordion} filterAccordion includeDesktop`} 
            key={category} 
            label={t(`organisms_post_filter_${category}`)}>
              {taxonomies.filter(taxonomy => taxonomy.nodeType === category).map((term) => (
                <Checkbox 
                  key={term.slug} 
                  name={category} 
                  label={term.name} 
                  id={term.slug} 
                  onChange={(e) => updateFilters((e.target as HTMLInputElement).checked, term.slug, term.nodeType)}
                  backgroundColor="light"
                />
              ))}</FilterAccordion>
        ))}
        </div>
        <div>
          <div className={styles.sidebarCtaHeadline}>{ctaHeadline}</div>
          <p className={generateClassName(['p2', styles.sidebarSubHeadline])}>{ctaSubtext}</p>
          <Buttons variants={['primary']}buttonColor="dark" buttons={ctaButtons} />
        </div>
      </div>
      <div className={styles.resultContainer}>
        <FilterSearchInput
          defaultValue={searchTerm}
          setSearchTerm={setSearchTerm}
        />

        <div className={generateClassName(['h4 semibold', styles.subheader])}>{subheadline}</div>

        <div className={styles.results}>
        {filteredPosts.map((post) => (
          <PostCard post={post} key={post.uri} />
        ))}
      </div>
      </div>
    </Wrapper>
  );
};
